import { useEffect } from "react";

/**
 * IntersectionObserver-driven reveal:
 *   [data-reveal] → adds `is-visible` when the element enters (or is already
 *   in) the viewport. Works after mount, after route changes, and — via a
 *   MutationObserver — for elements added later by lazy-loaded pages.
 *
 * Includes a safety net so an element that never intersects (bot, tiny
 * viewport, layout collapse) is force-revealed shortly after being added.
 */
export default function useReveal() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const reveal = (el) => {
      if (!el.classList.contains("is-visible")) el.classList.add("is-visible");
    };

    if (!("IntersectionObserver" in window)) {
      // No IO support — reveal everything, existing + future.
      document
        .querySelectorAll("[data-reveal]:not(.is-visible)")
        .forEach(reveal);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            reveal(e.target);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: "0px 0px -6% 0px" }
    );

    const pending = new WeakSet();
    const track = (el) => {
      if (pending.has(el) || el.classList.contains("is-visible")) return;
      pending.add(el);
      io.observe(el);
      // Per-element safety net: if the observer hasn't fired in 1.2s, reveal
      // anyway. Covers layout-collapse cases where IO never sees the element.
      window.setTimeout(() => reveal(el), 1200);
    };

    document.querySelectorAll("[data-reveal]").forEach(track);

    const mo = new MutationObserver((muts) => {
      muts.forEach((m) => {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.matches && n.matches("[data-reveal]")) track(n);
          if (n.querySelectorAll)
            n.querySelectorAll("[data-reveal]").forEach(track);
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });

    // Absolute backstop after 4s in case anything slipped through.
    const backstop = window.setTimeout(() => {
      document
        .querySelectorAll("[data-reveal]:not(.is-visible)")
        .forEach(reveal);
    }, 4000);

    return () => {
      io.disconnect();
      mo.disconnect();
      window.clearTimeout(backstop);
    };
  }, []);
}
